<template>
  <div class="conciliacao-page">
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <h5 class="mb-0">Conciliação Bancária</h5>

      <div class="d-flex align-items-center gap-2 flex-wrap">
      <button
        type="button"
        class="btn btn-success btn-sm d-flex align-items-center justify-content-center"
        @click="abrirImportacao('OFX')"
      >
        <i class="bi bi-upload me-2"></i>
        OFX
      </button>

      <button
        type="button"
        class="btn btn-success btn-sm d-flex align-items-center justify-content-center"
        @click="abrirImportacao('OPERADORA')"
      >
        <i class="bi bi-upload me-2"></i>
        Operadora
      </button>
      </div>
    </div>

    <!-- Filtros -->
    <ConciliacaoFiltros
      :filtros="filtros"
      :contas="contas"
      @aplicar="aplicarFiltros"
    />

    <ConciliacaoResumoCards :resumo="resumo" />

    <div class="mb-3">
      <div class="tabs-toolbar">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="alert alert-info mb-3">
      Carregando dados...
    </div>

    <div v-if="erro" class="alert alert-danger mb-3">
      {{ erro }}
    </div>

    <div v-if="sucesso" class="alert alert-success mb-3" role="status">
      {{ sucesso }}
    </div>    

    <div
      v-if="!loading && (!filtros.contaId || !filtros.competencia)"
      class="alert alert-secondary mb-3"
    >
      Selecione a conta financeira e a competência para carregar a conciliação.
    </div>

    <template v-if="!loading">
      <ConciliacaoBancoTabela
        v-if="activeTab === 'banco'"
        :items="bancoItems"
        :pagination="bancoPagination"
        :total-label="`Total: ${bancoPagination.total || 0}`"
        @conciliar="abrirModalManual"
        @conciliar-banco-operadora="abrirModalBancoOperadora"
        @detalhar="detalhar"
        @detalhar-banco-operadora="detalharBancoOperadora"
        @desfazer="desfazer"
        @desfazer-banco-operadora="desfazerBancoOperadora"
        @change-page="mudarPaginaBanco"
        @change-limit="mudarLimiteBanco"
      />

      <ConciliacaoMovimentacoesTabela
        v-else-if="activeTab === 'movimentacoes'"
        :items="movimentacoesItems"
        @conciliar="abrirModalManual"
        @detalhar="detalhar"
        @desfazer="desfazer"
      />

      <ConciliacaoOperadoraTabela
        v-else-if="activeTab === 'operadora'"
        :items="operadoraItems"
        :pagination="operadoraPagination"
        :total-label="`Total: ${operadoraPagination.total || 0}`"
        @conciliar="abrirModalManual"
        @regularizar-venda-cancelada="abrirRegularizacaoVendaCancelada"
        @detalhar="detalhar"
        @desfazer="desfazer"
        @change-page="mudarPaginaOperadora"
        @change-limit="mudarLimiteOperadora"
      />

      <ConciliacaoChargebackTabela
        v-else-if="activeTab === 'chargebacks'"
        :items="chargebackItems"
        :pagination="chargebackPagination"
        :total-label="`Total: ${chargebackPagination.total || 0}`"
        @detalhar="detalharChargeback"
        @tratar="tratarItemChargeback"
        @change-page="mudarPaginaChargebacks"
        @change-limit="mudarLimiteChargebacks"
      />

      <ConciliacaoHistoricoTabela
        v-else
        :items="historicoItems"
        :pagination="historicoPagination"
        @change-page="mudarPaginaHistorico"
        @change-limit="mudarLimiteHistorico"
      />
    </template>

    <ConciliacaoImportacaoModal
      v-if="showImportacao"
      :tipo="importacaoTipo"
      :contas="contas"
      :data-inicio="filtros.dataInicio"
      :data-fim="filtros.dataFim"
      @close="fecharImportacao"
      @importado="onImportado"
    />

    <ConciliacaoManualModal
      v-if="showManual && manualContext.item"
      :origem-tipo="manualContext.origemTipo"
      :item="manualContext.item"
      :data-inicio="filtros.dataInicio"
      :data-fim="filtros.dataFim"
      :modo="manualVisualizacao ? 'visualizacao' : 'edicao'"
      :vinculados="manualContext.vinculados"
      :taxa-operadora="manualContext.taxaOperadora"
      @close="fecharModalManual"
      @salvo="reloadActive"
    />

    <DesfazerConciliacaoModal
      v-if="showDesfazerModal && desfazerContext.item"
      :origem-tipo="desfazerContext.origemTipo"
      :item="desfazerContext.item"
      :salvando="desfazendoConciliacao"
      :possui-receita-criada-conciliacao="desfazerContext.possuiReceitaCriadaConciliacao"
      :possui-receita-abatimento-criada-conciliacao="desfazerContext.possuiReceitaAbatimentoCriadaConciliacao"
      :possui-despesa-criada-conciliacao="desfazerContext.possuiDespesaCriadaConciliacao"
      :possui-ajuste-pagamento-conciliacao="desfazerContext.possuiAjustePagamentoConciliacao"
      @close="fecharDesfazerModal"
      @confirmar="confirmarDesfazerConciliacao"
    />

    <div
      v-if="showDesfazerBancoOperadoraModal && desfazerBancoOperadoraContext.item"
      class="modal-overlay"
      @click.self="fecharDesfazerBancoOperadoraModal"
    >
      <div class="confirm-modal">
        <div class="confirm-modal__header">
          <div>
            <h6 class="mb-0">Desfazer conciliação Banco x Operadora</h6>
            <small>Confirme antes de desfazer este vínculo.</small>
          </div>

          <i
            class="bi bi-x-circle close-icon"
            @click="fecharDesfazerBancoOperadoraModal"
          ></i>
        </div>

        <div class="confirm-modal__body">
          <div class="alert alert-warning mb-3">
            Deseja desfazer esta conciliação Banco x Operadora?
          </div>

          <div class="confirm-grid">
            <div>
              <span>Item banco</span>
              <strong>#{{ desfazerBancoOperadoraContext.item?.id || '—' }}</strong>
            </div>

            <div>
              <span>Vínculo</span>
              <strong>#{{ desfazerBancoOperadoraContext.vinculoId || '—' }}</strong>
            </div>

            <div>
              <span>Valor</span>
              <strong>{{ formatCurrency(desfazerBancoOperadoraContext.item?.valor) }}</strong>
            </div>

            <div>
              <span>Movimento</span>
              <strong>{{ formatDate(desfazerBancoOperadoraContext.item?.dt_movimento) }}</strong>
            </div>
          </div>

          <div class="text-muted mt-2">
            Essa ação removerá apenas o vínculo entre o item bancário e o item da operadora.
          </div>
        </div>

        <div class="confirm-modal__footer">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="fecharDesfazerBancoOperadoraModal"
            :disabled="desfazendoBancoOperadora"
          >
            Cancelar
          </button>

          <button
            type="button"
            class="btn btn-danger btn-sm"
            @click="confirmarDesfazerBancoOperadora"
            :disabled="desfazendoBancoOperadora"
          >
            {{ desfazendoBancoOperadora ? 'Desfazendo...' : 'Confirmar e desfazer' }}
          </button>
        </div>
      </div>
    </div>    

    <ConciliacaoBancoOperadoraModal
      v-if="showBancoOperadora && bancoOperadoraItem"
      :item="bancoOperadoraItem"
      :modo="bancoOperadoraVisualizacao ? 'visualizacao' : 'edicao'"
      :vinculados="bancoOperadoraVinculados"
      @close="fecharModalBancoOperadora"
      @salvo="reloadActive"
    />

    <RegularizarVendaCanceladaModal
      v-if="showRegularizacaoVendaCancelada && regularizacaoVendaCanceladaItem"
      :item="regularizacaoVendaCanceladaItem"
      :salvando="regularizandoVendaCancelada"
      @close="fecharRegularizacaoVendaCancelada"
      @confirmar="confirmarRegularizacaoVendaCancelada"
    />

    <ConciliacaoChargebackDetalheModal
      v-if="showChargebackDetalhe && chargebackDetalheItem"
      :item="chargebackDetalheItem"
      @close="fecharModalChargeback"
    />

    <div
      v-if="showChargebackTratamentoModal && chargebackTratamentoContext.item"
      class="modal-overlay"
      @click.self="fecharTratamentoChargebackModal"
    >
      <div class="confirm-modal">
        <div class="confirm-modal__header">
          <div>
            <h6 class="mb-0">{{ chargebackTratamentoTitulo }}</h6>
            <small>Revise os dados antes de confirmar.</small>
          </div>

          <i
            class="bi bi-x-circle close-icon"
            @click="fecharTratamentoChargebackModal"
          ></i>
        </div>

        <div class="confirm-modal__body">
          <div class="alert alert-warning mb-3">
            {{ chargebackTratamentoMensagem }}
          </div>

          <div class="confirm-grid">
            <div>
              <span>Item</span>
              <strong>#{{ chargebackTratamentoContext.item?.id || '—' }}</strong>
            </div>

            <div>
              <span>Evento</span>
              <strong>{{ chargebackTratamentoContext.item?.descricao_evento || chargebackTratamentoContext.item?.tipo_evento || '—' }}</strong>
            </div>

            <div>
              <span>Data</span>
              <strong>{{ formatDate(chargebackTratamentoContext.item?.data_referencia || chargebackTratamentoContext.item?.data_vencimento || chargebackTratamentoContext.item?.data_venda) }}</strong>
            </div>

            <div>
              <span>Valor</span>
              <strong>{{ formatCurrency(chargebackTratamentoContext.item?.valor_evento || chargebackTratamentoContext.item?.valor_bruto || chargebackTratamentoContext.item?.valor_parcela || chargebackTratamentoContext.item?.valor_liquido) }}</strong>
            </div>
          </div>

          <div
            v-if="chargebackTratamentoContext.acao !== 'REABRIR'"
            class="mt-3"
          >
            <label class="form-label fw-bold">Observação</label>
            <textarea
              v-model="chargebackTratamentoContext.observacao"
              class="form-control"
              rows="3"
              placeholder="Informe uma observação para o tratamento, se necessário."
            ></textarea>
          </div>

          <div
            v-else
            class="text-muted mt-3"
          >
            Esta ação reabrirá o evento para ajuste e limpará o responsável/data de tratamento no backend.
          </div>
        </div>

        <div class="confirm-modal__footer">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="fecharTratamentoChargebackModal"
            :disabled="tratandoChargeback"
          >
            Cancelar
          </button>

          <button
            type="button"
            class="btn btn-warning btn-sm"
            @click="confirmarTratamentoChargeback"
            :disabled="tratandoChargeback"
          >
            {{ tratandoChargeback ? 'Salvando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { onMounted, reactive, ref, computed } from 'vue'
import axios from '@/services/axios'
import {
  obterResumo,
  listarBanco,
  listarMovimentacoes,
  listarOperadora,
  listarChargebacks,
  listarImportacoes,
  desfazerConciliacao,
  detalharConciliacao,
  desfazerBancoOperadoraConciliacao,
  reprocessarAutomaticoBancoOperadora,
  detalharBancoOperadoraConciliacao,
  tratarChargeback,
  regularizarVendaCanceladaLiquidada
} from '@/services/conciliacaoBancaria.api'

import ConciliacaoResumoCards from '@/components/Financeiro/Conciliacao/ConciliacaoResumoCards.vue'
import ConciliacaoFiltros from '@/components/Financeiro/Conciliacao/ConciliacaoFiltros.vue'
import ConciliacaoBancoTabela from '@/components/Financeiro/Conciliacao/ConciliacaoBancoTabela.vue'
import ConciliacaoOperadoraTabela from '@/components/Financeiro/Conciliacao/ConciliacaoOperadoraTabela.vue'
import ConciliacaoChargebackTabela from '@/components/Financeiro/Conciliacao/ConciliacaoChargebackTabela.vue'
import ConciliacaoHistoricoTabela from '@/components/Financeiro/Conciliacao/ConciliacaoHistoricoTabela.vue'
import ConciliacaoImportacaoModal from '@/components/Financeiro/Conciliacao/ConciliacaoImportacaoModal.vue'
import ConciliacaoManualModal from '@/components/Financeiro/Conciliacao/ConciliacaoManualModal.vue'
import ConciliacaoBancoOperadoraModal from '@/components/Financeiro/Conciliacao/ConciliacaoBancoOperadoraModal.vue'
import ConciliacaoChargebackDetalheModal from '@/components/Financeiro/Conciliacao/ConciliacaoChargebackDetalheModal.vue'
import RegularizarVendaCanceladaModal from '@/components/Financeiro/Conciliacao/RegularizarVendaCanceladaModal.vue'
import DesfazerConciliacaoModal from '@/components/Financeiro/Conciliacao/DesfazerConciliacaoModal.vue'
import ConciliacaoMovimentacoesTabela from '@/components/Financeiro/Conciliacao/ConciliacaoMovimentacoesTabela.vue'

const loading = ref(false)
const erro = ref('')
const sucesso = ref('')
const activeTab = ref('banco')

const bancoPage = ref(1)
const operadoraPage = ref(1)
const chargebackPage = ref(1)
const historicoPage = ref(1)

const showBancoOperadora = ref(false)
const bancoOperadoraItem = ref(null)

const showRegularizacaoVendaCancelada = ref(false)
const regularizacaoVendaCanceladaItem = ref(null)
const regularizandoVendaCancelada = ref(false)

const showChargebackDetalhe = ref(false)
const chargebackDetalheItem = ref(null)
const showChargebackTratamentoModal = ref(false)
const tratandoChargeback = ref(false)

const chargebackTratamentoContext = reactive({
  item: null,
  acao: '',
  observacao: ''
})

const bancoLimit = ref(10)
const operadoraLimit = ref(10)
const chargebackLimit = ref(10)
const historicoLimit = ref(10)

const tabs = [
  { key: 'banco', label: 'Banco x Sistema' },
  { key: 'movimentacoes', label: 'Movimentações' },
  { key: 'operadora', label: 'Operadora x Sistema' },
  { key: 'chargebacks', label: 'Ajustes da Operadora' },
  { key: 'historico', label: 'Histórico' }
]

const filtros = reactive({
  contaId: '',
  competencia: '',
  dataInicio: '',
  dataFim: '',
  status: '',
  tipoMovimentoBanco: '',
  operadoraNome: ''
})

const filtrosMinimosPreenchidos = computed(() =>
  !!filtros.contaId && !!filtros.competencia && !!filtros.dataInicio && !!filtros.dataFim
)

const chargebackTratamentoTitulo = computed(() => {
  const acao = String(chargebackTratamentoContext.acao || '').toUpperCase()

  if (acao === 'AJUSTAR') return 'Marcar evento como ajustado'
  if (acao === 'IGNORAR') return 'Ignorar evento'
  if (acao === 'REABRIR') return 'Reabrir evento para ajuste'

  return 'Tratar ajuste da operadora'
})

const chargebackTratamentoMensagem = computed(() => {
  const acao = String(chargebackTratamentoContext.acao || '').toUpperCase()

  if (acao === 'AJUSTAR') return 'Deseja marcar este evento como ajustado?'
  if (acao === 'IGNORAR') return 'Deseja ignorar este evento?'
  if (acao === 'REABRIR') return 'Deseja reabrir este evento para ajuste?'

  return 'Deseja salvar esta ação?'
})

const resumo = ref({ banco: {}, operadora: {} })
const contas = ref([])
const bancoItems = ref([])
const operadoraItems = ref([])
const chargebackItems = ref([])
const historicoItems = ref([])
const movimentacoesItems = ref([])

const bancoPagination = reactive({
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 10
})
const operadoraPagination = reactive({
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 10
})
const chargebackPagination = reactive({
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 10
})

const historicoPagination = reactive({
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 10
})

async function mudarLimiteBanco(limit) {
  bancoLimit.value = limit
  bancoPage.value = 1
  await carregarBanco()
}

const showImportacao = ref(false)
const importacaoTipo = ref('OFX')

const showManual = ref(false)
const manualContext = reactive({
  origemTipo: '',
  item: null,
  vinculados: [],
  taxaOperadora: null
})

const showDesfazerModal = ref(false)

const desfazerContext = reactive({
  origemTipo: '',
  item: null,
  possuiReceitaCriadaConciliacao: false,
  possuiReceitaAbatimentoCriadaConciliacao: false,
  possuiDespesaCriadaConciliacao: false,
  possuiAjustePagamentoConciliacao: false
})

const desfazendoConciliacao = ref(false)
const showDesfazerBancoOperadoraModal = ref(false)
const desfazendoBancoOperadora = ref(false)

const desfazerBancoOperadoraContext = reactive({
  item: null,
  vinculoId: null
})

async function mudarLimiteOperadora(limit) {
  operadoraLimit.value = limit
  operadoraPage.value = 1
  await carregarOperadora()
}

async function mudarLimiteHistorico(limit) {
  historicoLimit.value = limit
  historicoPage.value = 1
  await carregarHistorico()
}

const manualVisualizacao = ref(false)
const bancoOperadoraVisualizacao = ref(false)
const bancoOperadoraVinculados = ref([])

onMounted(async () => {
  await carregarContas()
  await reloadAll()
})

async function mudarLimiteChargebacks(limit) {
  chargebackLimit.value = limit
  chargebackPage.value = 1
  await carregarChargebacks()
}

async function detalhar(payload) {
  try {
    const { data } = await detalharConciliacao({
      origemTipo: payload.origemTipo,
      origemItemId: payload.item.id
    })

    manualContext.origemTipo = payload.origemTipo
    manualContext.item = data?.item || payload.item
    manualContext.vinculados = Array.isArray(data?.vinculados) ? data.vinculados : []
    manualContext.taxaOperadora = data?.taxa_operadora || null
    manualVisualizacao.value = true
    showManual.value = true
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao detalhar conciliação.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)
  }
}

function competenciaParaIntervalo(competencia) {
  if (!competencia) {
    return { dataInicio: '', dataFim: '' }
  }

  const [ano, mes] = String(competencia).split('-').map(Number)
  if (!ano || !mes) {
    return { dataInicio: '', dataFim: '' }
  }

  const primeiroDia = `${ano}-${String(mes).padStart(2, '0')}-01`
  const ultimoDiaDate = new Date(ano, mes, 0)
  const ultimoDia = `${ano}-${String(mes).padStart(2, '0')}-${String(ultimoDiaDate.getDate()).padStart(2, '0')}`

  return {
    dataInicio: primeiroDia,
    dataFim: ultimoDia
  }
}

async function detalharBancoOperadora(payload) {
  try {
    const { data } = await detalharBancoOperadoraConciliacao({
      ofxItemId: payload.item.id
    })

    bancoOperadoraItem.value = data?.item || payload.item
    bancoOperadoraVinculados.value = Array.isArray(data?.vinculados) ? data.vinculados : []
    bancoOperadoraVisualizacao.value = true
    showBancoOperadora.value = true
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao detalhar conciliação Banco x Operadora.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)
  }
}

async function carregarContas() {
  try {
    const res = await axios.get('/financeiro/contas-financeiras', {
      params: { page: 1, limit: 1000 }
    })
    contas.value = Array.isArray(res.data?.data) ? res.data.data : []
  } catch (e) {
    console.error('Erro ao carregar contas financeiras:', e)
  }
}

function abrirRegularizacaoVendaCancelada(payload) {
  const item = payload?.item

  if (!item?.id) {
    erro.value = 'Não foi possível identificar a venda a regularizar.'
    return
  }

  regularizacaoVendaCanceladaItem.value = item
  showRegularizacaoVendaCancelada.value = true
}

function fecharRegularizacaoVendaCancelada() {
  if (regularizandoVendaCancelada.value) return

  showRegularizacaoVendaCancelada.value = false
  regularizacaoVendaCanceladaItem.value = null
}

async function confirmarRegularizacaoVendaCancelada() {
  const item = regularizacaoVendaCanceladaItem.value

  if (!item?.id) {
    erro.value = 'Não foi possível identificar a venda a regularizar.'
    return
  }

  regularizandoVendaCancelada.value = true
  erro.value = ''
  sucesso.value = ''

  try {
    const { data } =
      await regularizarVendaCanceladaLiquidada({
        item_id: item.id
      })

    showRegularizacaoVendaCancelada.value = false
    regularizacaoVendaCanceladaItem.value = null

    const impacto =
      Number(data?.impacto_liquido || 0)

    sucesso.value =
      `Venda cancelada liquidada regularizada com sucesso. Impacto líquido: ${formatCurrency(impacto)}.`

    await reloadAll()

    setTimeout(() => {
      sucesso.value = ''
    }, 4000)

  } catch (e) {
    sucesso.value = ''

    erro.value =
      e?.response?.data?.error ||
      'Erro ao regularizar venda cancelada liquidada.'

    setTimeout(() => {
      erro.value = ''
    }, 5000)

  } finally {
    regularizandoVendaCancelada.value = false
  }
}

async function tratarItemChargeback(payload) {
  const item = payload?.item
  const acao = String(payload?.acao || '').toUpperCase()

  if (!item?.id || !acao) {
    erro.value = 'Não foi possível identificar o item ou a ação.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)

    return
  }

  chargebackTratamentoContext.item = item
  chargebackTratamentoContext.acao = acao
  chargebackTratamentoContext.observacao = ''
  showChargebackTratamentoModal.value = true
}

function fecharTratamentoChargebackModal() {
  if (tratandoChargeback.value) return

  showChargebackTratamentoModal.value = false
  chargebackTratamentoContext.item = null
  chargebackTratamentoContext.acao = ''
  chargebackTratamentoContext.observacao = ''
}

async function confirmarTratamentoChargeback() {
  const item = chargebackTratamentoContext.item
  const acao = String(chargebackTratamentoContext.acao || '').toUpperCase()

  if (!item?.id || !acao) {
    erro.value = 'Não foi possível identificar o item ou a ação.'
    return
  }

  tratandoChargeback.value = true
  erro.value = ''
  sucesso.value = ''

  try {
    await tratarChargeback({
      item_id: item.id,
      acao,
      observacao:
        acao === 'REABRIR'
          ? null
          : (chargebackTratamentoContext.observacao || null)
    })

    showChargebackTratamentoModal.value = false
    chargebackTratamentoContext.item = null
    chargebackTratamentoContext.acao = ''
    chargebackTratamentoContext.observacao = ''

    if (acao === 'AJUSTAR') {
      sucesso.value = 'Evento marcado como ajustado com sucesso.'
    } else if (acao === 'IGNORAR') {
      sucesso.value = 'Evento ignorado com sucesso.'
    } else if (acao === 'REABRIR') {
      sucesso.value = 'Evento reaberto para ajuste com sucesso.'
    } else {
      sucesso.value = 'Tratamento salvo com sucesso.'
    }

    await carregarChargebacks()
    await carregarResumo()

    setTimeout(() => {
      sucesso.value = ''
    }, 2000)
  } catch (e) {
    sucesso.value = ''
    erro.value = e?.response?.data?.error || 'Erro ao tratar ajuste da operadora.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)
  } finally {
    tratandoChargeback.value = false
  }
}

function buildParams() {
  return {
    contaId: filtros.contaId || undefined,
    dataInicio: filtros.dataInicio || undefined,
    dataFim: filtros.dataFim || undefined,
    status: filtros.status || undefined,
    tipoMovimentoBanco: filtros.tipoMovimentoBanco || undefined,
    operadoraNome: filtros.operadoraNome || undefined
  }
}

async function reloadAll() {
  erro.value = ''

  if (!filtrosMinimosPreenchidos.value) {
    resumo.value = { banco: {}, operadora: {} }
    bancoItems.value = []
    movimentacoesItems.value = []
    operadoraItems.value = []
    chargebackItems.value = []
    historicoItems.value = []
    bancoPagination.page = 1
    bancoPagination.totalPages = 1
    bancoPagination.total = 0
    operadoraPagination.page = 1
    operadoraPagination.totalPages = 1
    operadoraPagination.total = 0
    chargebackPagination.page = 1
    chargebackPagination.totalPages = 1
    chargebackPagination.total = 0
    return
  }

  loading.value = true
  try {
    await Promise.all([
      carregarResumo(),
      carregarBanco(),
      carregarMovimentacoes(),
      carregarOperadora(),
      carregarChargebacks(),
      carregarHistorico()
    ])
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao carregar dados da conciliação.'
  } finally {
    loading.value = false
  }
}

async function carregarResumo() {
  const { data } = await obterResumo(buildParams())
  resumo.value = data || { banco: {}, operadora: {} }
}

async function carregarBanco() {
  const { data } = await listarBanco({
    ...buildParams(),
    page: bancoPage.value,
    limit: bancoLimit.value
  })

  bancoItems.value = Array.isArray(data?.data) ? data.data : []
  bancoPagination.page = data?.page || 1
  bancoPagination.totalPages = data?.totalPages || 1
  bancoPagination.total = data?.total || 0
  bancoPagination.limit = bancoLimit.value
}

async function carregarMovimentacoes() {
  if (!filtrosMinimosPreenchidos.value) {
    movimentacoesItems.value = []
    return
  }

  const { data } = await listarMovimentacoes({
    contaId: filtros.contaId || undefined,
    dataInicio: filtros.dataInicio || undefined,
    dataFim: filtros.dataFim || undefined
  })

  movimentacoesItems.value = Array.isArray(data?.dias) ? data.dias : []
}

async function carregarOperadora() {
  const { data } = await listarOperadora({
    ...buildParams(),
    page: operadoraPage.value,
    limit: operadoraLimit.value
  })

  operadoraItems.value = Array.isArray(data?.data) ? data.data : []
  operadoraPagination.page = data?.page || 1
  operadoraPagination.totalPages = data?.totalPages || 1
  operadoraPagination.total = data?.total || 0
  operadoraPagination.limit = operadoraLimit.value
}

async function carregarChargebacks() {
  const { data } = await listarChargebacks({
    ...buildParams(),
    page: chargebackPage.value,
    limit: chargebackLimit.value
  })

  chargebackItems.value = Array.isArray(data?.data) ? data.data : []
  chargebackPagination.page = data?.page || 1
  chargebackPagination.totalPages = data?.totalPages || 1
  chargebackPagination.total = data?.total || 0
  chargebackPagination.limit = chargebackLimit.value
}

async function carregarHistorico() {
  if (!filtrosMinimosPreenchidos.value) {
    historicoItems.value = []
    historicoPagination.page = 1
    historicoPagination.totalPages = 1
    historicoPagination.total = 0
    historicoPagination.limit = historicoLimit.value
    return
  }

  const { data } = await listarImportacoes({
    contaId: filtros.contaId || undefined,
    dataInicio: filtros.dataInicio || undefined,
    dataFim: filtros.dataFim || undefined,
    page: historicoPage.value,
    limit: historicoLimit.value
  })

  historicoItems.value = Array.isArray(data?.data) ? data.data : []
  historicoPagination.page = data?.page || 1
  historicoPagination.totalPages = data?.totalPages || 1
  historicoPagination.total = data?.total || 0
  historicoPagination.limit = historicoLimit.value
}



async function aplicarFiltros(novosFiltros) {
  filtros.contaId = novosFiltros.contaId || ''
  filtros.competencia = novosFiltros.competencia || ''

  if (!filtros.contaId || !filtros.competencia) {
    filtros.status = ''
    filtros.tipoMovimentoBanco = ''
    filtros.operadoraNome = ''
    filtros.dataInicio = ''
    filtros.dataFim = ''
  } else {
    filtros.status = novosFiltros.status || ''
    filtros.tipoMovimentoBanco = novosFiltros.tipoMovimentoBanco || ''
    filtros.operadoraNome = novosFiltros.operadoraNome || ''

    const intervalo = competenciaParaIntervalo(filtros.competencia)
    filtros.dataInicio = intervalo.dataInicio
    filtros.dataFim = intervalo.dataFim
  }

  bancoPage.value = 1
  operadoraPage.value = 1
  chargebackPage.value = 1
  historicoPage.value = 1

  await reloadAll()
}

async function mudarPaginaBanco(page) {
  bancoPage.value = page
  await carregarBanco()
}

async function mudarPaginaOperadora(page) {
  operadoraPage.value = page
  await carregarOperadora()
}

async function mudarPaginaChargebacks(page) {
  chargebackPage.value = page
  await carregarChargebacks()
}

async function mudarPaginaHistorico(page) {
  historicoPage.value = page
  await carregarHistorico()
}

function abrirImportacao(tipo) {
  importacaoTipo.value = tipo
  showImportacao.value = true
}

function fecharImportacao() {
  showImportacao.value = false
}

function detalharChargeback(payload) {
  chargebackDetalheItem.value = payload.item
  showChargebackDetalhe.value = true
}

function fecharModalChargeback() {
  showChargebackDetalhe.value = false
  chargebackDetalheItem.value = null
}

async function onImportado(resultado) {
  sucesso.value = ''
  if (resultado?.ja_reconhecido === true) {
    sucesso.value = resultado.message ||
      'Arquivo já reconhecido. Nenhum repasse ou vínculo adicional foi criado.'
    await reloadAll()
    return
  }

  try {
    if (filtros.contaId && filtros.dataInicio && filtros.dataFim) {
      await reprocessarAutomaticoBancoOperadora({
        contaId: Number(filtros.contaId),
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      })
    }
  } catch (e) {
    console.error('Erro ao reprocessar Banco x Operadora após importação:', e)
  }

  await reloadAll()
}

function abrirModalManual(payload) {
  manualVisualizacao.value = false
  manualContext.origemTipo = payload.origemTipo
  manualContext.item = payload.item
  manualContext.vinculados = []
  manualContext.taxaOperadora = null
  showManual.value = true
}

function fecharModalManual() {
  showManual.value = false
  manualVisualizacao.value = false
  manualContext.origemTipo = ''
  manualContext.item = null
  manualContext.vinculados = []
  manualContext.taxaOperadora = null
}

function abrirModalBancoOperadora(payload) {
  bancoOperadoraVisualizacao.value = false
  bancoOperadoraItem.value = payload.item
  bancoOperadoraVinculados.value = []
  showBancoOperadora.value = true
}

function fecharModalBancoOperadora() {
  showBancoOperadora.value = false
  bancoOperadoraVisualizacao.value = false
  bancoOperadoraItem.value = null
  bancoOperadoraVinculados.value = []
}

async function desfazer(payload) {
  const item = payload?.item
  const origemTipo = String(payload?.origemTipo || '').toUpperCase()

  if (!item?.id || !origemTipo) {
    erro.value = 'Não foi possível identificar o item da conciliação para desfazer.'
    return
  }

  try {
    const { data } = await detalharConciliacao({
      origemTipo,
      origemItemId: item.id
    })

    const vinculados = Array.isArray(data?.vinculados) ? data.vinculados : []

    const possuiReceitaCriadaConciliacao = vinculados.some((vinc) =>
      [
        'RECEITA_CRIADA_CONCILIACAO',
        'RECEITA_REG_OPERADORA'
      ].includes(
        String(vinc?.tipo_vinculo || '').toUpperCase()
      )
    )

    const possuiReceitaAbatimentoCriadaConciliacao = vinculados.some((vinc) =>
      Boolean(vinc?.receita_abatimento_criada_conciliacao) ||
      (
        String(vinc?.tipo_vinculo || '').toUpperCase() === 'RECEITA_ABATE_CONCILIACAO' &&
        String(vinc?.movimento_sistema_tabela || '') === 'receitas_gerais_pagamentos' &&
        Boolean(vinc?.receita_abatimento_criada_payload)
      )
    )

    const possuiDespesaCriadaConciliacao = vinculados.some((vinc) =>
      String(vinc?.tipo_vinculo || '').toUpperCase() === 'DESPESA_CRIADA_CONCILIACAO'
    )

    const possuiAjustePagamentoConciliacao = vinculados.some((vinc) =>
      Boolean(vinc?.ajuste_pagamento_conciliacao)
    )    

    desfazerContext.origemTipo = origemTipo
    desfazerContext.item = item
    desfazerContext.possuiReceitaCriadaConciliacao = possuiReceitaCriadaConciliacao
    desfazerContext.possuiReceitaAbatimentoCriadaConciliacao = possuiReceitaAbatimentoCriadaConciliacao
    desfazerContext.possuiDespesaCriadaConciliacao = possuiDespesaCriadaConciliacao
    desfazerContext.possuiAjustePagamentoConciliacao = possuiAjustePagamentoConciliacao
    showDesfazerModal.value = true
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao verificar os vínculos da conciliação.'
  }
}

function fecharDesfazerModal() {
  if (desfazendoConciliacao.value) return

  showDesfazerModal.value = false
  desfazerContext.origemTipo = ''
  desfazerContext.item = null
  desfazerContext.possuiReceitaCriadaConciliacao = false
  desfazerContext.possuiReceitaAbatimentoCriadaConciliacao = false
  desfazerContext.possuiAjustePagamentoConciliacao = false
  desfazerContext.possuiDespesaCriadaConciliacao = false
}

async function confirmarDesfazerConciliacao(opcoes) {
  const item = desfazerContext.item
  const origemTipo = String(desfazerContext.origemTipo || '').toUpperCase()

  if (!item?.id || !origemTipo) {
    erro.value = 'Não foi possível identificar o item da conciliação para desfazer.'
    return
  }

  desfazendoConciliacao.value = true
  erro.value = ''
  sucesso.value = ''

  try {
    const { data } = await desfazerConciliacao({
      origem_item_tipo: origemTipo,
      origem_item_id: item.id,
      excluir_receita_criada: opcoes?.excluir_receita_criada === true,
      excluir_receita_abatimento_criada: opcoes?.excluir_receita_abatimento_criada === true,
      excluir_despesa_criada: opcoes?.excluir_despesa_criada === true,
      motivo: opcoes?.motivo || null
    })

    const receitasExcluidas = Array.isArray(data?.receitas_criadas_excluidas)
      ? data.receitas_criadas_excluidas
      : []

    const receitasAbatimentoExcluidas = Array.isArray(data?.receitas_abatimento_criadas_excluidas)
      ? data.receitas_abatimento_criadas_excluidas
      : []      

    const despesasExcluidas = Array.isArray(data?.despesas_criadas_excluidas)
      ? data.despesas_criadas_excluidas
      : []      

    const algumaReceitaExcluida = receitasExcluidas.some((r) => r.excluido)
    const algumaReceitaAbatimentoExcluida = receitasAbatimentoExcluidas.some((r) => r.excluido)
    const algumaDespesaExcluida = despesasExcluidas.some((r) => r.excluido)

    if (opcoes?.excluir_receita_abatimento_criada && algumaReceitaAbatimentoExcluida) {
      sucesso.value = 'Conciliação desfeita e Receita de abatimento criada pela conciliação excluída.'
    } else if (opcoes?.excluir_receita_criada && algumaReceitaExcluida) {
      sucesso.value = 'Conciliação desfeita e Receita Geral criada pela conciliação excluída.'
    } else if (opcoes?.excluir_despesa_criada && algumaDespesaExcluida) {
      sucesso.value = 'Conciliação desfeita e Despesa criada pela conciliação excluída.'
    } else if (opcoes?.excluir_receita_abatimento_criada && !algumaReceitaAbatimentoExcluida) {
      sucesso.value = 'Conciliação desfeita. Nenhuma Receita de abatimento foi excluída automaticamente.'
    } else if (opcoes?.excluir_receita_criada && !algumaReceitaExcluida) {
      sucesso.value = 'Conciliação desfeita. Nenhuma Receita Geral foi excluída automaticamente.'
    } else if (opcoes?.excluir_despesa_criada && !algumaDespesaExcluida) {
      sucesso.value = 'Conciliação desfeita. Nenhuma Despesa foi excluída automaticamente.'
    } else {
      sucesso.value = 'Conciliação desfeita. Os lançamentos financeiros vinculados foram mantidos.'
    }

    // Fecha o modal diretamente, sem chamar fecharDesfazerModal(),
    // porque neste momento desfazendoConciliacao ainda está true.
    showDesfazerModal.value = false
    desfazerContext.origemTipo = ''
    desfazerContext.item = null
    desfazerContext.possuiReceitaCriadaConciliacao = false
    desfazerContext.possuiReceitaAbatimentoCriadaConciliacao = false
    desfazerContext.possuiAjustePagamentoConciliacao = false
    desfazerContext.possuiDespesaCriadaConciliacao = false

    await reloadAll()

    setTimeout(() => {
      sucesso.value = ''
    }, 2000)
  } catch (e) {
    sucesso.value = ''
    erro.value = e?.response?.data?.error || 'Erro ao desfazer conciliação.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)
  } finally {
    desfazendoConciliacao.value = false
  }
}

async function desfazerBancoOperadora(payload) {
  if (!payload?.item?.banco_operadora_vinculo_id) {
    erro.value = 'Não foi possível identificar o vínculo Banco x Operadora para desfazer.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)

    return
  }

  desfazerBancoOperadoraContext.item = payload.item
  desfazerBancoOperadoraContext.vinculoId = payload.item.banco_operadora_vinculo_id
  showDesfazerBancoOperadoraModal.value = true
}

function fecharDesfazerBancoOperadoraModal() {
  if (desfazendoBancoOperadora.value) return

  showDesfazerBancoOperadoraModal.value = false
  desfazerBancoOperadoraContext.item = null
  desfazerBancoOperadoraContext.vinculoId = null
}

async function confirmarDesfazerBancoOperadora() {
  if (!desfazerBancoOperadoraContext.vinculoId) {
    erro.value = 'Não foi possível identificar o vínculo Banco x Operadora para desfazer.'
    return
  }

  desfazendoBancoOperadora.value = true
  erro.value = ''
  sucesso.value = ''

  try {
    await desfazerBancoOperadoraConciliacao({
      vinculo_id: desfazerBancoOperadoraContext.vinculoId
    })

    showDesfazerBancoOperadoraModal.value = false
    desfazerBancoOperadoraContext.item = null
    desfazerBancoOperadoraContext.vinculoId = null

    sucesso.value = 'Conciliação Banco x Operadora desfeita com sucesso.'

    await reloadAll()

    setTimeout(() => {
      sucesso.value = ''
    }, 2000)
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao desfazer conciliação Banco x Operadora.'

    setTimeout(() => {
      erro.value = ''
    }, 4000)
  } finally {
    desfazendoBancoOperadora.value = false
  }
}

async function reloadActive() {
  try {
    await carregarResumo()

    if (activeTab.value === 'banco') {
      await carregarBanco()
    } else if (activeTab.value === 'movimentacoes') {
      await carregarMovimentacoes()
    } else if (activeTab.value === 'operadora') {
      await carregarOperadora()
    } else if (activeTab.value === 'chargebacks') {
      await carregarChargebacks()
    } else if (activeTab.value === 'historico') {
      await carregarHistorico()
    }

    fecharModalManual()
    fecharModalBancoOperadora()
  } catch (e) {
    erro.value = e?.response?.data?.error || 'Erro ao atualizar dados da conciliação.'
  }
}

function formatDate(v) {
  if (!v) return '—'

  const s = String(v).trim()

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-')
    return `${d}/${m}/${y}`
  }

  const dt = new Date(s)
  if (Number.isNaN(dt.getTime())) return '—'

  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const d = String(dt.getDate()).padStart(2, '0')

  return `${d}/${m}/${y}`
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}
</script>

<style scoped>
.conciliacao-page {
  width: 100%;
}

.tabs-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tab-btn {
  border: 1px solid #ced4da;
  background: #fff;
  color: #495057;
  border-radius: 0.375rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.tab-btn.active {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.table-action-cell {
  text-align: center !important;
  white-space: nowrap;
}

.table-action-btn {
  width: 78px;
  padding: 0.18rem 0.45rem;
  font-size: 0.74rem;
  line-height: 1.1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.confirm-modal {
  background: #fff;
  width: 100%;
  max-width: 620px;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.25);
}

.confirm-modal__header {
  background: #f3f4f6;
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #d1d5db;
}

.confirm-modal__header small {
  color: #6b7280;
}

.confirm-modal__body {
  padding: 1rem;
}

.confirm-modal__footer {
  padding: 0.85rem 1rem;
  border-top: 1px solid #d1d5db;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.confirm-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.confirm-grid > div {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 0.65rem 0.75rem;
}

.confirm-grid span {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 0.2rem;
}

.close-icon {
  font-size: 1.35rem;
  cursor: pointer;
}

@media (max-width: 768px) {
  .content-card {
    padding: 0.75rem;
  }

  .tab-btn {
    width: 100%;
    text-align: center;
  }
  .confirm-grid {
    grid-template-columns: 1fr;
  }  
}
</style>
